import { useEffect, useState } from 'react'
import { Switch } from '@/components/ui/switch'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { useUserPreferences, useUpdateUserPreferences } from '@/hooks/queries'
import { useAuth } from '@/contexts/AuthContext'

/**
 * Settings — Ransomware leak-site activity (Plan B Plan 2 T20).
 *
 * Three controls covering spec §6.6:
 *   - Daily briefing peer-activity section opt-in.
 *   - Critical alert subscription when claims surge in the user's sector.
 *   - Per-user override for the burst-alert threshold (NULL → system default).
 *   - Real-time toast opt-in (off by default — explicit user action).
 *
 * Mirrors the toggle pattern in `NotificationsSection.tsx` so the user-prefs
 * mutation is shared. The numeric threshold input is debounced via local
 * state so we don't fire a PUT on every keystroke.
 */
export function LeakSitesSection() {
  const { user } = useAuth()
  const userId = user?.sub ?? ''
  const { data: preferences } = useUserPreferences(userId)
  const updatePreferences = useUpdateUserPreferences()

  // Defaults match the migration column defaults so unauthenticated /
  // empty-preferences renders look right out of the box.
  const prefs = preferences ?? {
    briefingIncludesRansomware: true,
    ransomwareBurstAlertsEnabled: true,
    ransomwareBurstAlertThreshold: null as number | null,
    ransomwareRealtimeToastsEnabled: false,
  }

  // Threshold input is locally controlled and committed onBlur to avoid a
  // PUT per keystroke. `null` (no override) renders as an empty string.
  const [threshold, setThreshold] = useState<string>(
    prefs.ransomwareBurstAlertThreshold == null
      ? ''
      : String(prefs.ransomwareBurstAlertThreshold),
  )

  // Sync local state when the server value arrives or changes elsewhere.
  useEffect(() => {
    setThreshold(
      prefs.ransomwareBurstAlertThreshold == null
        ? ''
        : String(prefs.ransomwareBurstAlertThreshold),
    )
  }, [prefs.ransomwareBurstAlertThreshold])

  function commitThreshold() {
    const trimmed = threshold.trim()
    const next = trimmed === '' ? null : Number(trimmed)
    // Reject NaN / out-of-range silently and reset to last saved value.
    if (next !== null && (!Number.isFinite(next) || next < 1 || next > 100)) {
      setThreshold(
        prefs.ransomwareBurstAlertThreshold == null
          ? ''
          : String(prefs.ransomwareBurstAlertThreshold),
      )
      return
    }
    if (next === prefs.ransomwareBurstAlertThreshold) return
    updatePreferences.mutate({
      userId,
      ransomwareBurstAlertThreshold: next,
    })
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">Show in daily briefing</p>
          <p className="text-xs text-muted-foreground">
            Peer ransomware activity in your sector and region.
          </p>
        </div>
        <Switch
          checked={prefs.briefingIncludesRansomware ?? true}
          onCheckedChange={(checked) =>
            updatePreferences.mutate({
              userId,
              briefingIncludesRansomware: checked,
            })
          }
          aria-label="Show ransomware peer activity in daily briefing"
        />
      </div>
      <Separator />
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">Burst alerts</p>
          <p className="text-xs text-muted-foreground">
            Critical alert when claim volume in your sector spikes.
          </p>
        </div>
        <Switch
          checked={prefs.ransomwareBurstAlertsEnabled ?? true}
          onCheckedChange={(checked) =>
            updatePreferences.mutate({
              userId,
              ransomwareBurstAlertsEnabled: checked,
            })
          }
          aria-label="Enable ransomware burst alerts"
        />
      </div>
      <Separator />
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <p className="text-sm font-medium">Burst alert threshold</p>
          <p className="text-xs text-muted-foreground">
            Claims per 24h that trigger an alert. Leave blank for system
            default (5/24h).
          </p>
        </div>
        <Input
          type="number"
          min={1}
          max={100}
          inputMode="numeric"
          placeholder="5"
          className="w-24"
          value={threshold}
          onChange={(e) => setThreshold(e.target.value)}
          onBlur={commitThreshold}
          aria-label="Ransomware burst alert threshold"
        />
      </div>
      <Separator />
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">Real-time toasts</p>
          <p className="text-xs text-muted-foreground">
            In-app toast when a matching claim drops. Off by default.
          </p>
        </div>
        <Switch
          checked={prefs.ransomwareRealtimeToastsEnabled ?? false}
          onCheckedChange={(checked) =>
            updatePreferences.mutate({
              userId,
              ransomwareRealtimeToastsEnabled: checked,
            })
          }
          aria-label="Enable real-time ransomware toasts"
        />
      </div>
    </div>
  )
}
